import React from "react";

import { useParsed } from "@refinedev/core";

import { useTinaPoll } from "@/pages/tina/hooks/use-tina-poll";
import { ItemsShowScaffold } from "@/pages/tina/items/components/items-show-scaffold";
import { CancelledPanel } from "@/pages/tina/items/components/panels/cancelled-panel";
import { DiscoveriesPanel } from "@/pages/tina/items/components/panels/discoveries-panel";
import { DonePanel } from "@/pages/tina/items/components/panels/done-panel";
import { DuplicatePanel } from "@/pages/tina/items/components/panels/duplicate-panel";
import { FailedPanel } from "@/pages/tina/items/components/panels/failed-panel";
import { FailedRetryablePanel } from "@/pages/tina/items/components/panels/failed-retryable-panel";
import { HitlPanel } from "@/pages/tina/items/components/panels/hitl-panel";
import { InProgressPanel } from "@/pages/tina/items/components/panels/in-progress-panel";
import { SkippedPanel } from "@/pages/tina/items/components/panels/skipped-panel";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { isDiscoverSeedItem } from "@/pages/tina/items/item-context";
import type {
  ItemStatus,
  PipelineItem,
  StageExecutionRead,
} from "@/types/tina";

const LIVE_STATUSES: ReadonlySet<ItemStatus> = new Set([
  "pending",
  "in_progress",
  "waiting_on_primary",
  "failed_retryable",
]);

export const ItemsShow = () => {
  const { id } = useParsed();
  const idStr = String(id ?? "");
  const repo = useItemsRepository();

  const item = useTinaPoll(["tina", "items", idStr], () => repo.get(idStr), {
    enabled: !!idStr,
    shouldPoll: (data) => !!data && LIVE_STATUSES.has(data.status),
  });

  const stages = useTinaPoll(
    ["tina", "items", idStr, "stages"],
    () => repo.getStages(idStr),
    {
      enabled: !!idStr,
      shouldPoll: () => LIVE_STATUSES.has(item.data?.status ?? "done"),
    },
  );

  const shouldLoadDiscoveries = !!item.data && isDiscoverSeedItem(item.data);
  const discoveries = useTinaPoll(
    ["tina", "items", idStr, "discoveries"],
    () => repo.listDiscoveries(idStr),
    {
      enabled: !!idStr && shouldLoadDiscoveries,
      shouldPoll: () => LIVE_STATUSES.has(item.data?.status ?? "done"),
    },
  );

  const sortedStages = React.useMemo(
    () =>
      (stages.data ?? []).slice().sort((a, b) => {
        if (a.started_at === b.started_at) return 0;
        if (a.started_at === null) return 1;
        if (b.started_at === null) return -1;
        return a.started_at.localeCompare(b.started_at);
      }),
    [stages.data],
  );

  if (!item.data) return <div className="p-6">Loading…</div>;

  return (
    <ItemsShowScaffold
      item={item.data}
      stages={sortedStages}
      main={
        <MainPanelDispatcher
          item={item.data}
          stages={sortedStages}
          discoveries={discoveries.data?.data ?? []}
          discoveriesTotal={discoveries.data?.total ?? 0}
          discoveriesLoading={discoveries.isLoading}
        />
      }
    />
  );
};

export function MainPanelDispatcher({
  item,
  stages,
  discoveries,
  discoveriesTotal,
  discoveriesLoading,
}: {
  item: PipelineItem;
  stages: StageExecutionRead[];
  discoveries: PipelineItem[];
  discoveriesTotal: number;
  discoveriesLoading: boolean;
}) {
  if (isDiscoverSeedItem(item)) {
    return (
      <DiscoveriesPanel
        item={item}
        discoveries={discoveries}
        total={discoveriesTotal}
        isLoading={discoveriesLoading}
      />
    );
  }

  if (item.final_outcome === "duplicate") {
    return <DuplicatePanel item={item} />;
  }

  switch (item.status) {
    case "skipped":
      return <SkippedPanel item={item} />;
    case "failed":
      return <FailedPanel item={item} />;
    case "cancelled":
      return <CancelledPanel item={item} />;
    case "failed_retryable":
      return <FailedRetryablePanel item={item} stages={stages} />;
    case "pending":
    case "in_progress":
    case "waiting_on_primary":
      return <InProgressPanel item={item} stagesCount={stages.length} />;
    case "done":
      return <DonePanel item={item} />;
    case "duplicate":
      return <DuplicatePanel item={item} />;
    case "hitl":
      return <HitlPanel item={item} />;
    default:
      return (
        <div className="text-sm text-muted-foreground">
          [panel for status="{item.status}" not yet implemented · stages:{" "}
          {stages.length}]
        </div>
      );
  }
}
